import { useRef, useEffect, type CSSProperties } from 'react'

interface KineticGridProps {
  background?: string
  dotColor?: string
  lineColor?: string
  trailColor?: string
  /** Brand colors dots blend toward as the cursor nears them (hex strings). */
  glowColors?: string[]
  spacing?: number // grid spacing in px
  radius?: number // cursor attraction radius in px
  strength?: number // 1-10 attraction strength
  trail?: boolean // show cursor trail line
  style?: CSSProperties
}

function hexToRgb(hex: string) {
  const n = parseInt(hex.replace('#', ''), 16)
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 }
}

function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t
}

// Blends across an arbitrary number of color stops arranged around a wheel,
// so a dot's hue shifts smoothly as its angle to the cursor changes.
function wheelColor(stops: { r: number; g: number; b: number }[], t: number) {
  const n = stops.length
  const scaled = (((t % 1) + 1) % 1) * n
  const i = Math.floor(scaled) % n
  const f = scaled - Math.floor(scaled)
  const a = stops[i]
  const b = stops[(i + 1) % n]
  return { r: lerp(a.r, b.r, f), g: lerp(a.g, b.g, f), b: lerp(a.b, b.b, f) }
}

// A reactive dot grid that is pulled toward the cursor within a chosen
// radius, with a trail line that follows the mouse as it moves.
export function KineticGrid({
  background = '#000000',
  dotColor = '#FFFFFF',
  lineColor = '#2563EB',
  trailColor = '#2664EB',
  glowColors,
  spacing = 50,
  radius = 200,
  strength = 4,
  trail = true,
  style,
}: KineticGridProps) {
  const hostRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const mouseRef = useRef({ x: -9999, y: -9999, active: false })
  const trailRef = useRef<{ x: number; y: number; t: number }[]>([])

  useEffect(() => {
    const host = hostRef.current
    const canvas = canvasRef.current
    if (!host || !canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const GAP = Math.max(8, spacing)
    const R = Math.max(1, radius)
    const PULL = (Math.max(1, Math.min(10, strength)) / 10) * 4
    const baseRgb = hexToRgb(dotColor)
    const baseLineRgb = hexToRgb(lineColor)
    const glowStops = glowColors?.map(hexToRgb)

    let W = 1
    let H = 1
    let cols: { hx: number; hy: number; x: number; y: number; vx: number; vy: number }[][] = []
    let dots: { hx: number; hy: number; x: number; y: number; vx: number; vy: number }[] = []

    const build = (mw?: number, mh?: number) => {
      const r = host.getBoundingClientRect()
      W = Math.max(1, Math.floor(mw ?? r.width))
      H = Math.max(1, Math.floor(mh ?? r.height))
      const dpr = window.devicePixelRatio || 1
      canvas.width = Math.floor(W * dpr)
      canvas.height = Math.floor(H * dpr)
      canvas.style.width = W + 'px'
      canvas.style.height = H + 'px'
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

      cols = []
      dots = []
      const nCols = Math.floor(W / GAP) + 2
      const nRows = Math.floor(H / GAP) + 2
      for (let c = 0; c < nCols; c++) {
        const col: typeof dots = []
        for (let rIdx = 0; rIdx < nRows; rIdx++) {
          const hx = c * GAP
          const hy = rIdx * GAP
          const d = { hx, hy, x: hx, y: hy, vx: 0, vy: 0 }
          col.push(d)
          dots.push(d)
        }
        cols.push(col)
      }
    }

    build()

    const ro =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver((entries) => {
            // contentRect is the reliable size — getBoundingClientRect
            // can be stale (pre-layout) when the effect first runs.
            const cr = entries[0]?.contentRect
            build(cr?.width, cr?.height)
          })
        : null
    ro?.observe(host)

    // Listen on window (not the host div) — the headline/button sit on top
    // of the grid as sibling elements, so host-scoped listeners would go
    // dead under them since the pointer never actually hits `host` there.
    const setMouse = (clientX: number, clientY: number) => {
      const r = canvas.getBoundingClientRect()
      const mx = clientX - r.left
      const my = clientY - r.top
      mouseRef.current.x = mx
      mouseRef.current.y = my
      mouseRef.current.active = mx >= 0 && mx <= W && my >= 0 && my <= H
      const now = performance.now()
      const t = trailRef.current
      t.push({ x: mx, y: my, t: now })
      if (t.length > 80) t.shift()
    }

    const onMove = (e: MouseEvent) => setMouse(e.clientX, e.clientY)
    const onLeave = () => {
      mouseRef.current.active = false
      mouseRef.current.x = -9999
      mouseRef.current.y = -9999
    }
    const onTouch = (e: TouchEvent) => {
      const t = e.touches[0]
      if (t) setMouse(t.clientX, t.clientY)
    }
    const onOut = (e: MouseEvent) => {
      if (!e.relatedTarget) onLeave()
    }

    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseout', onOut)
    window.addEventListener('touchmove', onTouch, { passive: true })
    window.addEventListener('touchend', onLeave)

    let raf = 0
    const frame = () => {
      const m = mouseRef.current
      ctx.clearRect(0, 0, W, H)

      // Update dot physics: spring home + attraction toward cursor.
      for (const d of dots) {
        let ax = (d.hx - d.x) * 0.08
        let ay = (d.hy - d.y) * 0.08
        if (m.active) {
          const dx = m.x - d.x
          const dy = m.y - d.y
          const dist = Math.sqrt(dx * dx + dy * dy)
          if (dist < R && dist > 0.001) {
            const f = (1 - dist / R) * PULL
            ax += (dx / dist) * f
            ay += (dy / dist) * f
          }
        }
        d.vx = (d.vx + ax) * 0.82
        d.vy = (d.vy + ay) * 0.82
        d.x += d.vx
        d.y += d.vy
      }

      // Grid mesh lines (brighten near the cursor).
      for (let c = 0; c < cols.length; c++) {
        for (let rIdx = 0; rIdx < cols[c].length; rIdx++) {
          const d = cols[c][rIdx]
          const right = cols[c + 1]?.[rIdx]
          const down = cols[c]?.[rIdx + 1]
          const ldx = m.x - d.x
          const ldy = m.y - d.y
          const dist = Math.sqrt(ldx * ldx + ldy * ldy)
          const prox = m.active ? Math.max(0, 1 - dist / R) : 0
          let strokeStyle: string = lineColor
          if (glowStops && glowStops.length > 0 && prox > 0) {
            const angle = Math.atan2(ldy, ldx)
            const glow = wheelColor(glowStops, angle / (2 * Math.PI))
            const r = lerp(baseLineRgb.r, glow.r, prox)
            const g = lerp(baseLineRgb.g, glow.g, prox)
            const b = lerp(baseLineRgb.b, glow.b, prox)
            strokeStyle = `rgb(${r}, ${g}, ${b})`
          }
          if (right) {
            ctx.globalAlpha = 0.06 + prox * 0.7
            ctx.strokeStyle = strokeStyle
            ctx.lineWidth = 0.5 + prox * 1.5
            ctx.beginPath()
            ctx.moveTo(d.x, d.y)
            ctx.lineTo(right.x, right.y)
            ctx.stroke()
          }
          if (down) {
            ctx.globalAlpha = 0.06 + prox * 0.7
            ctx.strokeStyle = strokeStyle
            ctx.lineWidth = 0.5 + prox * 1.5
            ctx.beginPath()
            ctx.moveTo(d.x, d.y)
            ctx.lineTo(down.x, down.y)
            ctx.stroke()
          }
        }
      }

      // Dots — blend toward the brand glow colors as the cursor nears them,
      // instead of just darkening toward the base dot color.
      for (const d of dots) {
        const dx = m.x - d.x
        const dy = m.y - d.y
        const dist = Math.sqrt(dx * dx + dy * dy)
        const prox = m.active ? Math.max(0, 1 - dist / R) : 0
        ctx.globalAlpha = 0.22 + prox * 0.78
        if (glowStops && glowStops.length > 0 && prox > 0) {
          const angle = Math.atan2(dy, dx)
          const glow = wheelColor(glowStops, angle / (2 * Math.PI))
          const r = lerp(baseRgb.r, glow.r, prox)
          const g = lerp(baseRgb.g, glow.g, prox)
          const b = lerp(baseRgb.b, glow.b, prox)
          ctx.fillStyle = `rgb(${r}, ${g}, ${b})`
        } else {
          ctx.fillStyle = dotColor
        }
        ctx.beginPath()
        ctx.arc(d.x, d.y, 0.8 + prox * 2.2, 0, 2 * Math.PI)
        ctx.fill()
      }

      // Cursor trail line — visible on plain mouse move, fades out.
      if (trail) {
        const now = performance.now()
        const tr = trailRef.current
        ctx.lineCap = 'round'
        ctx.lineJoin = 'round'
        for (let i = 1; i < tr.length; i++) {
          const a = tr[i - 1]
          const b = tr[i]
          const age = now - b.t
          if (age > 260) continue
          ctx.globalAlpha = Math.max(0, 1 - age / 260) * 0.85
          ctx.strokeStyle = trailColor
          ctx.lineWidth = 2
          ctx.beginPath()
          ctx.moveTo(a.x, a.y)
          ctx.lineTo(b.x, b.y)
          ctx.stroke()
        }
      }

      ctx.globalAlpha = 1
      raf = requestAnimationFrame(frame)
    }
    raf = requestAnimationFrame(frame)

    return () => {
      cancelAnimationFrame(raf)
      ro?.disconnect()
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseout', onOut)
      window.removeEventListener('touchmove', onTouch)
      window.removeEventListener('touchend', onLeave)
    }
  }, [background, dotColor, lineColor, trailColor, glowColors, spacing, radius, strength, trail])

  return (
    <div
      ref={hostRef}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        background,
        ...style,
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
        }}
      />
    </div>
  )
}
